import React, { useState, useEffect } from 'react';

type Theme = 'light' | 'dark';

export const useTheme = () => {
    // Get initial theme from localStorage or system preference
    const getInitialTheme = (): Theme => {
        try {
            const savedTheme = localStorage.getItem('theme') as Theme;
            if (savedTheme) return savedTheme;

            // Check system preference
            return window.matchMedia('(prefers-color-scheme: dark)').matches
                ? 'dark'
                : 'light';
        } catch (error) {
            console.warn('Failed to get initial theme:', error);
            return 'light'; // Provide a default fallback
        }
    };

    const [theme, setTheme] = useState<Theme>(getInitialTheme);

    // Update theme in localStorage and HTML class
    const updateTheme = (newTheme: Theme) => {
        try {
            const root = window.document.documentElement;
            
            // Remove old theme class and add new one
            root.classList.remove('light', 'dark');
            root.classList.add(newTheme);
            
            // Save to localStorage
            localStorage.setItem('theme', newTheme);
            setTheme(newTheme);
        } catch (error) {
            console.warn('Failed to update theme:', error);
            // Still update the state even if localStorage fails
            setTheme(newTheme);
        }
    };

    // Initialize theme on mount
    useEffect(() => {
        updateTheme(theme);
    }, []);

    // Toggle between light and dark
    const toggleTheme = () => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        updateTheme(newTheme);
    };

    return {
        theme,
        setTheme: updateTheme,
        toggleTheme,
        isDarkMode: theme === 'dark'
    };
};

export default useTheme;
